// Portrait components — procedural SVG faces and per-persona scene backdrops.
// Personas listed in PHOTO_PERSONAS use a real PNG portrait from
// /public/portraits/<id>.png; everyone else gets PortraitFace + PortraitScene.

import type { CSSProperties } from 'react';
import type { Persona } from '@/lib/types';
import { PERSONAS_BY_ID } from '@/data/personas';

// Persona ids that have a curated photo portrait at /portraits/<id>.jpg.
// Keep filenames in sync with this set. Exported so other modules (e.g.
// DiscoverScreen's cache warm-up) can gate their preload loop on it.
export const PHOTO_PERSONAS = new Set<string>([
  'achao',
  'ajie',
  'akai',
  'amao',
  'aming',
  'aqi',
  'aqiang',
  'atao',
  'dalong',
  'dawei',
  'duoduo',
  'haoran',
  'jiajia',
  'jiawei',
  'jingyi',
  'laochen',
  'laoli',
  'laowang',
  'lili',
  'linlaoshi',
  'meiling',
  'minmin',
  'wangjiaoshou',
  'xiaofang',
  'xiaohei',
  'xiaoma',
  'xiaomei',
  'xiaoyu',
  'xiaoyun',
  'xiaoze',
  'xinran',
  'xinxin',
  'xueer',
  'yiyi',
  'ziqing',
]);

const FEM_NAME_CHARS = ['美', '玲', '雪', '莉', '静', '紫', '芳', '雨', '晴', '怡', '林'];

function inferArchetype(persona: Persona): string {
  // Specific overrides for the personas where the bio strongly suggests a look
  const overrides: Record<string, string> = {
    laowang:    'cap',       // taxi driver, cap on
    dawei:      'cap',       // fisherman
    xiaoma:     'cap',       // grassland herder
    laoli:      'oldMaleBald',
    laochen:    'oldMaleBald',
    wangjiaoshou: 'glassesMale',
    jingyi:     'glassesFem',
    ziqing:     'bun',
    meiling:    'sleekFem',
    dalong:     'manBun',
    xueer:      'longFem',
    lili:       'tiedBack',
    aqiang:     'shortMale',
    ajie:       'shortMale',
    aming:      'shortMale',
    atao:       'slickBack',
    xiaomei:    'shortFem',
    linlaoshi:  'tiedBack',
    xiaofang:   'ponytail',
    xiaoyu:     'longFem',
  };
  return overrides[persona.id] || (FEM_NAME_CHARS.some(c => persona.name.includes(c)) ? 'longFem' : 'shortMale');
}

function PortraitFace({ persona }: { persona: Persona }) {
  const archetype = inferArchetype(persona);
  const skinTone = '#F4D9C0';
  const skinShade = '#E8C5A6';
  const hairColor = persona.age >= 50 ? '#5C4A3D' : '#1F1812';
  const grayStreak = persona.age >= 50;

  // Common: head + neck + body/collar (rendered last for layering)
  const Head = (
    <>
      {/* Body/collar (layered behind head bottom) */}
      <ellipse cx="50" cy="105" rx="40" ry="28" fill={persona.grad[1]} opacity="0.85" />
      {/* Neck */}
      <rect x="44" y="68" width="12" height="14" fill={skinShade} />
      {/* Head */}
      <ellipse cx="50" cy="46" rx="20" ry="24" fill={skinTone} />
      {/* Subtle cheek shading */}
      <ellipse cx="38" cy="54" rx="3" ry="2" fill={skinShade} opacity="0.5" />
      <ellipse cx="62" cy="54" rx="3" ry="2" fill={skinShade} opacity="0.5" />
    </>
  );

  // Eyes — small dots, slightly rounded
  const Eyes = (
    <>
      <ellipse cx="42" cy="50" rx="1.5" ry="2" fill="#1A1614" />
      <ellipse cx="58" cy="50" rx="1.5" ry="2" fill="#1A1614" />
    </>
  );

  // Mouth — small curved line
  const Mouth = <path d="M45 60 Q50 62.5 55 60" stroke="#3A2A22" strokeWidth="1.2" fill="none" strokeLinecap="round" />;

  // Hair shapes per archetype
  const Hair = (() => {
    switch (archetype) {
      case 'shortMale':
        return <path d="M30 38 Q30 24 50 24 Q70 24 70 40 Q66 32 60 32 L40 32 Q34 32 30 40 Z" fill={hairColor} />;
      case 'shortFem':
        return <path d="M28 42 Q28 22 50 22 Q72 22 72 42 Q70 32 64 30 L36 30 Q30 32 28 42 Z" fill={hairColor} />;
      case 'longFem':
        return (
          <>
            <path d="M26 50 Q26 22 50 22 Q74 22 74 50 L74 72 Q72 60 70 50 L66 38 L34 38 L30 50 Q28 60 26 72 Z" fill={hairColor} />
          </>
        );
      case 'ponytail':
        return (
          <>
            <path d="M30 40 Q30 22 50 22 Q70 22 70 40 Q68 32 62 30 L38 30 Q32 32 30 40 Z" fill={hairColor} />
            <ellipse cx="76" cy="48" rx="5" ry="14" fill={hairColor} transform="rotate(15 76 48)" />
          </>
        );
      case 'tiedBack':
        return (
          <>
            <path d="M30 42 Q30 24 50 24 Q70 24 70 42 Q66 36 50 36 Q34 36 30 42 Z" fill={hairColor} />
            <ellipse cx="50" cy="32" rx="22" ry="8" fill={hairColor} />
          </>
        );
      case 'bun':
        return (
          <>
            <path d="M32 44 Q32 28 50 28 Q68 28 68 44 Q64 38 50 38 Q36 38 32 44 Z" fill={hairColor} />
            <circle cx="50" cy="20" r="7" fill={hairColor} />
            {grayStreak && <path d="M40 32 Q42 26 46 24" stroke="#A89A8C" strokeWidth="1.5" fill="none" />}
          </>
        );
      case 'manBun':
        return (
          <>
            <path d="M32 42 Q32 26 50 26 Q68 26 68 42 Q66 34 60 32 L40 32 Q34 34 32 42 Z" fill={hairColor} />
            <circle cx="50" cy="20" r="5" fill={hairColor} />
          </>
        );
      case 'sleekFem':
        return (
          <>
            <path d="M28 44 Q28 22 50 22 Q72 22 72 44 L72 60 L68 50 L66 38 L34 38 L32 50 L28 60 Z" fill={hairColor} />
          </>
        );
      case 'slickBack':
        return <path d="M30 36 Q30 22 50 22 Q70 22 70 36 Q68 28 62 30 L38 30 Q32 28 30 36 Z" fill={hairColor} />;
      case 'cap':
        return (
          <>
            <path d="M26 38 Q28 30 50 28 Q72 30 74 38 L78 42 L72 36 Q66 28 50 26 Q34 28 28 36 L22 42 Z" fill={persona.accent} />
            <rect x="22" y="38" width="56" height="4" fill={persona.accent} />
            <ellipse cx="78" cy="42" rx="6" ry="2" fill={persona.accent} opacity="0.9" />
          </>
        );
      case 'oldMaleBald':
        return (
          <>
            {/* Side hair only */}
            <path d="M28 50 Q26 40 30 36 Q32 42 32 50 Z" fill={hairColor} />
            <path d="M72 50 Q74 40 70 36 Q68 42 68 50 Z" fill={hairColor} />
            {grayStreak && <path d="M28 48 Q30 44 32 48" stroke="#A89A8C" strokeWidth="1.5" fill="none" />}
          </>
        );
      case 'glassesMale':
        return (
          <>
            <path d="M30 38 Q30 24 50 24 Q70 24 70 38 Q66 32 60 32 L40 32 Q34 32 30 38 Z" fill={hairColor} />
            {grayStreak && <path d="M55 30 L60 32" stroke="#A89A8C" strokeWidth="1.5" fill="none" />}
          </>
        );
      case 'glassesFem':
        return (
          <>
            <path d="M28 42 Q28 22 50 22 Q72 22 72 42 L72 56 L68 46 L66 36 L34 36 L32 46 L28 56 Z" fill={hairColor} />
          </>
        );
      default:
        return <path d="M30 38 Q30 24 50 24 Q70 24 70 40 Q66 32 60 32 L40 32 Q34 32 30 40 Z" fill={hairColor} />;
    }
  })();

  // Glasses overlay (drawn on top of eyes)
  const Glasses = (archetype === 'glassesMale' || archetype === 'glassesFem') && (
    <>
      <circle cx="42" cy="50" r="5" fill="none" stroke="#1A1614" strokeWidth="1.2" />
      <circle cx="58" cy="50" r="5" fill="none" stroke="#1A1614" strokeWidth="1.2" />
      <line x1="47" y1="50" x2="53" y2="50" stroke="#1A1614" strokeWidth="1.2" />
    </>
  );

  // Beard for select older men
  const Beard = (persona.id === 'laowang' || persona.id === 'dawei') && (
    <path d="M40 64 Q42 70 50 70 Q58 70 60 64" fill={hairColor} opacity="0.8" />
  );

  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <PortraitScene persona={persona} />
      {Head}
      {Hair}
      {Eyes}
      {Glasses}
      {Mouth}
      {Beard}
    </svg>
  );
}

// PortraitScene — place/context backdrop drawn behind the face.
// Each persona gets recognizable elements from their world (coffee cup, mountain,
// taxi roof, ice crystals…) so the avatar feels environmental, not generic.
function PortraitScene({ persona }: { persona: Persona }) {
  const wash = 'rgba(255,255,255,0.42)';
  const light = 'rgba(255,255,255,0.58)';
  const dark = 'rgba(26,22,20,0.18)';

  switch (persona.id) {
    case 'xiaomei': // hutong cafe — coffee cup + steam
      return (
        <g>
          <path d="M8 18 Q6 12 10 8 M14 16 Q12 10 16 6 M20 18 Q18 12 22 8" stroke={light} strokeWidth="1.4" fill="none" strokeLinecap="round" />
          <ellipse cx="84" cy="80" rx="11" ry="3.5" fill={wash} />
          <path d="M73 80 L76 95 L92 95 L95 80 Z" fill={wash} />
          <path d="M95 82 Q102 84 102 88 Q102 92 95 92" stroke={wash} strokeWidth="2" fill="none" />
        </g>
      );
    case 'linlaoshi': // kindergarten — ABC blocks
      return (
        <g>
          <rect x="6" y="10" width="12" height="12" fill={wash} />
          <text x="12" y="20" fontSize="9" fill="rgba(26,22,20,0.35)" fontWeight="bold" textAnchor="middle">A</text>
          <rect x="82" y="14" width="10" height="10" fill={light} />
          <text x="87" y="22" fontSize="7" fill="rgba(26,22,20,0.35)" fontWeight="bold" textAnchor="middle">你</text>
          <circle cx="78" cy="6" r="3" fill={wash} />
        </g>
      );
    case 'aming': // bubble tea — boba pearls + straw
      return (
        <g>
          <circle cx="10" cy="14" r="2.5" fill={dark} />
          <circle cx="18" cy="8" r="2" fill={dark} />
          <circle cx="6" cy="22" r="2" fill={dark} />
          <rect x="86" y="6" width="3" height="22" fill={wash} transform="rotate(15 87 17)" />
          <circle cx="92" cy="14" r="3" fill={dark} />
        </g>
      );
    case 'xiaofang': // uni student — books + leaves
      return (
        <g>
          <rect x="4" y="14" width="18" height="3" fill={wash} />
          <rect x="6" y="17" width="16" height="3" fill={light} />
          <rect x="3" y="20" width="20" height="3" fill={wash} />
          <path d="M85 8 Q82 12 85 16 Q88 12 85 8" fill={light} />
        </g>
      );
    case 'xiaoyu': // terracotta army — warrior silhouette
      return (
        <g>
          <path d="M82 8 Q80 12 80 16 L80 26 L88 26 L88 16 Q88 12 86 10 Q84 8 82 8 Z" fill={dark} opacity="0.5" />
          <path d="M82 6 Q84 4 86 6 L84 8 Z" fill={dark} opacity="0.5" />
          <rect x="6" y="20" width="14" height="4" fill={wash} />
          <rect x="4" y="22" width="18" height="2" fill={wash} />
        </g>
      );
    case 'ajie': // Shanghai food — noodle bowl + skyline
      return (
        <g>
          <rect x="78" y="6" width="3" height="18" fill={wash} />
          <rect x="84" y="2" width="3" height="22" fill={wash} />
          <rect x="90" y="8" width="3" height="16" fill={wash} />
          <ellipse cx="14" cy="92" rx="12" ry="3" fill={wash} />
          <path d="M2 92 Q4 100 14 100 Q24 100 26 92 Z" fill={wash} />
          <path d="M8 86 Q6 80 10 78 M14 86 Q12 80 16 78 M20 86 Q18 80 22 78" stroke={light} strokeWidth="1.2" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'meiling': // HK fashion — skyline + lipstick
      return (
        <g>
          <rect x="2" y="14" width="3" height="14" fill={wash} />
          <rect x="6" y="8" width="3" height="20" fill={light} />
          <rect x="10" y="12" width="3" height="16" fill={wash} />
          <rect x="14" y="6" width="3" height="22" fill={wash} />
          <rect x="18" y="10" width="3" height="18" fill={light} />
          <rect x="86" y="6" width="4" height="10" fill={dark} />
          <rect x="85" y="16" width="6" height="3" fill={light} />
        </g>
      );
    case 'aqiang': // Shenzhen tech — code brackets + screen
      return (
        <g>
          <text x="8" y="22" fontSize="14" fill={wash} fontFamily="monospace" fontWeight="bold">{'{ }'}</text>
          <rect x="80" y="8" width="14" height="10" fill={wash} />
          <rect x="80" y="8" width="14" height="2" fill={dark} opacity="0.4" />
          <rect x="85" y="18" width="4" height="2" fill={wash} />
        </g>
      );
    case 'laoli': // Yunnan market — mushrooms + mountain
      return (
        <g>
          <path d="M0 22 L8 12 L14 18 L22 8 L30 16 L30 25 L0 25 Z" fill={wash} />
          <path d="M82 18 Q82 14 86 14 Q90 14 90 18 L90 22 L82 22 Z" fill={light} />
          <rect x="85" y="22" width="2" height="4" fill={wash} />
          <ellipse cx="80" cy="92" rx="3" ry="2" fill={wash} />
        </g>
      );
    case 'xueer': // Harbin ice — snowflakes + crystal
      return (
        <g>
          <path d="M10 12 L10 22 M5 17 L15 17 M6.5 13.5 L13.5 20.5 M13.5 13.5 L6.5 20.5" stroke={light} strokeWidth="1.2" fill="none" />
          <path d="M84 6 L84 14 M80 10 L88 10" stroke={light} strokeWidth="1.1" fill="none" />
          <path d="M88 18 L88 24 M85 21 L91 21" stroke={wash} strokeWidth="1" fill="none" />
          <path d="M76 24 L80 16 L84 24 Z" fill={wash} />
        </g>
      );
    case 'laowang': // Beijing taxi — hutong roof + lantern
      return (
        <g>
          <path d="M0 18 L14 8 L28 18 L28 22 L0 22 Z" fill={dark} opacity="0.4" />
          <rect x="2" y="22" width="24" height="3" fill={dark} opacity="0.5" />
          <ellipse cx="86" cy="14" rx="5" ry="6" fill={light} />
          <rect x="84" y="8" width="4" height="2" fill={dark} opacity="0.4" />
          <rect x="84" y="20" width="4" height="2" fill={dark} opacity="0.4" />
        </g>
      );
    case 'dawei': // Qingdao fisher — fish + waves
      return (
        <g>
          <path d="M0 20 Q4 16 8 20 T16 20 T24 20 T32 20" stroke={light} strokeWidth="1.5" fill="none" />
          <path d="M0 24 Q4 20 8 24 T16 24 T24 24 T32 24" stroke={wash} strokeWidth="1.2" fill="none" />
          <path d="M82 14 L92 12 L94 16 L92 20 L82 18 Z" fill={wash} />
          <circle cx="91" cy="15" r="0.8" fill={dark} />
        </g>
      );
    case 'lili': // Wuhan doctor — medical cross + heartbeat
      return (
        <g>
          <rect x="84" y="8" width="4" height="14" fill="rgba(255,255,255,0.85)" />
          <rect x="79" y="13" width="14" height="4" fill="rgba(255,255,255,0.85)" />
          <path d="M0 20 L6 20 L8 14 L11 26 L14 16 L17 22 L24 22" stroke={light} strokeWidth="1.4" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'atao': // Macau casino — chips + dice
      return (
        <g>
          <circle cx="10" cy="14" r="6" fill={wash} stroke={light} strokeWidth="1" />
          <circle cx="10" cy="14" r="3" fill={dark} opacity="0.3" />
          <circle cx="20" cy="20" r="5" fill={light} />
          <rect x="84" y="8" width="10" height="10" fill={wash} transform="rotate(12 89 13)" />
          <circle cx="86" cy="11" r="0.7" fill={dark} opacity="0.6" />
          <circle cx="92" cy="15" r="0.7" fill={dark} opacity="0.6" />
        </g>
      );
    case 'wangjiaoshou': // Beijing prof — scroll + brush
      return (
        <g>
          <rect x="4" y="14" width="22" height="6" fill={wash} />
          <ellipse cx="4" cy="17" rx="2" ry="3" fill={light} />
          <ellipse cx="26" cy="17" rx="2" ry="3" fill={light} />
          <path d="M82 6 L86 22 L84 24 L80 8 Z" fill={dark} opacity="0.5" />
          <path d="M86 22 L88 26 L84 26 Z" fill={dark} opacity="0.6" />
        </g>
      );
    case 'ziqing': // Suzhou silk — flowing pattern + thread
      return (
        <g>
          <path d="M0 14 Q6 10 12 14 Q18 18 24 14 Q30 10 32 14" stroke={light} strokeWidth="1.5" fill="none" />
          <path d="M0 20 Q6 16 12 20 Q18 24 24 20 Q30 16 32 20" stroke={wash} strokeWidth="1.2" fill="none" />
          <circle cx="86" cy="14" r="5" fill={wash} />
          <path d="M86 14 L92 8 M86 14 L92 20" stroke={dark} strokeWidth="0.8" opacity="0.4" />
        </g>
      );
    case 'laochen': // Chongqing hotpot — chili + flame
      return (
        <g>
          <path d="M6 8 Q10 6 12 10 L14 22 Q14 26 10 26 Q6 26 6 22 Z" fill="rgba(231,75,60,0.65)" />
          <path d="M9 6 L11 9 L8 9 Z" fill={dark} opacity="0.5" />
          <path d="M82 22 Q80 16 84 12 Q86 16 86 20 Q88 16 90 18 Q90 22 86 24 Q84 26 82 22" fill={light} />
          <path d="M84 22 Q83 18 85 16 Q86 18 86 20" fill="rgba(231,75,60,0.55)" />
        </g>
      );
    case 'jingyi': // Beijing editor — book + pen
      return (
        <g>
          <rect x="4" y="14" width="18" height="14" fill={wash} />
          <line x1="13" y1="14" x2="13" y2="28" stroke={dark} strokeWidth="0.5" opacity="0.4" />
          <line x1="6" y1="18" x2="11" y2="18" stroke={dark} strokeWidth="0.5" opacity="0.3" />
          <line x1="15" y1="18" x2="20" y2="18" stroke={dark} strokeWidth="0.5" opacity="0.3" />
          <path d="M84 6 L92 14 L90 16 L82 8 Z" fill={dark} opacity="0.5" />
          <path d="M82 8 L80 10 L82 12 Z" fill={dark} opacity="0.6" />
        </g>
      );
    case 'dalong': // Hangzhou tea — mountains + tea steam
      return (
        <g>
          <path d="M0 20 L6 12 L12 18 L18 10 L26 18 L32 14 L32 26 L0 26 Z" fill={wash} />
          <path d="M0 22 L8 16 L14 20 L20 14 L28 20 L32 18 L32 26 L0 26 Z" fill={light} opacity="0.6" />
          <path d="M82 8 Q80 4 84 0 M86 8 Q84 4 88 0 M90 8 Q88 4 92 0" stroke={light} strokeWidth="1.2" fill="none" strokeLinecap="round" />
          <ellipse cx="86" cy="12" rx="6" ry="2" fill={wash} />
          <path d="M80 12 L82 18 L90 18 L92 12 Z" fill={wash} />
        </g>
      );
    case 'xiaoma': // Inner Mongolia — horse silhouette + grass horizon
      return (
        <g>
          <line x1="0" y1="22" x2="32" y2="22" stroke={light} strokeWidth="0.5" />
          <path d="M0 22 Q4 20 8 22 T16 22 T24 22 T32 22" stroke={wash} strokeWidth="1" fill="none" />
          <path d="M76 12 Q78 6 84 6 Q88 6 90 10 L92 12 L92 18 L88 22 L88 28 L84 28 L84 22 L80 22 L80 28 L76 28 L76 18 Z" fill={dark} opacity="0.55" />
          <path d="M74 8 L78 10 L76 12 Z" fill={dark} opacity="0.5" />
        </g>
      );
    default:
      return null;
  }
}

interface PortraitProps {
  /** Curated persona id; resolved against PERSONAS_BY_ID. Ignored if `persona` is given. */
  id?: string;
  /** Pass the full persona object directly — required for custom (HSK-mode) personas. */
  persona?: Persona;
  className?: string;
  style?: CSSProperties;
}

/** Parse 'custom-1' / 'custom-12' / etc. into a 1-based file number, or null
 *  if the slug doesn't match the expected shape. Kept defensive so a corrupted
 *  field on an old snapshot can't crash the render. */
function customPortraitNumber(slug: string | undefined): number | null {
  if (!slug) return null;
  const m = /^custom-(\d+)$/.exec(slug);
  if (!m?.[1]) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function Portrait({ id, persona: passed, className = '', style = {} }: PortraitProps) {
  const persona = passed ?? (id ? PERSONAS_BY_ID[id] : undefined);
  if (!persona) {
    return <div className={className} style={{ width: '100%', height: '100%', background: '#ddd', ...style }} />;
  }
  const customN = customPortraitNumber(persona.customPortraitId);
  const hasPhoto = PHOTO_PERSONAS.has(persona.id);
  return (
    <div
      className={className}
      style={{
        width: '100%', height: '100%',
        background: `linear-gradient(135deg, ${persona.grad[0]}, ${persona.grad[1]})`,
        overflow: 'hidden',
        ...style,
      }}
    >
      {customN !== null ? (
        <img
          src={`/portraits/custom/${customN}.jpg`}
          alt={persona.en}
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : hasPhoto ? (
        <img
          src={`/portraits/${persona.id}.jpg`}
          alt={persona.en}
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <PortraitFace persona={persona} />
      )}
    </div>
  );
}


// Dropped per-persona PortraitXiaomei/Ajie/Laowang/Xiaofang/Dalong overrides:
// the beta defined them but never referenced them. PortraitFace + archetype
// inference covers all 20 personas. Reintroduce as named exports here if you
// want hand-tuned overrides for specific personas in the future.
